import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {readonlyState} from '@malt/utils';
import type {SearchResponse, SearchProfilesRequest, FetchError} from '@api';
import {useApis, useBaseUrl, useErrorHandler} from '~/composables';
import {useSearchUrlStore} from './search-url.store';

export const useProfilesStore = defineStore('profiles', () => {
    const {searchApi} = useApis();
    const {handleSearchProfilesApiError} = useErrorHandler();

    const initialSearchIsLoading = ref(false);
    const initialSearchResponse = ref<SearchResponse>();
    const remoteFallbackIsLoading = ref(false);
    const remoteFallbackSearchResponse = ref<SearchResponse>();
    const europaFallbackIsLoading = ref(false);
    const europaFallbackSearchResponse = ref<SearchResponse>();
    const searchId = computed(() => initialSearchResponse.value?.searchId);
    const isLoading = computed(() => initialSearchIsLoading.value && remoteFallbackIsLoading.value && europaFallbackIsLoading.value);
    const totalResultCount = computed(
        () =>
            (initialSearchResponse.value?.pagination.totalElements ?? 0) +
            (remoteFallbackSearchResponse.value?.pagination.totalElements ?? 0) +
            (europaFallbackSearchResponse.value?.pagination.totalElements ?? 0),
    );
    const searchUrlStore = useSearchUrlStore();

    async function fetchProfiles() {
        initialSearchIsLoading.value = true;
        // also set fallbacks loading state to true because we will make a new initial search which will invalidate fallback results
        remoteFallbackIsLoading.value = true;
        europaFallbackIsLoading.value = true;
        initialSearchResponse.value = await getProfiles(searchUrlStore.toOpenApiRequestParams());
        initialSearchIsLoading.value = false;
    }

    async function fetchRemoteFallbackProfiles() {
        const initialSearchFallbackUrl = initialSearchResponse.value?.fallback;
        const initialSearchIsALocatedSearch = initialSearchResponse.value?.searchType === 'LOCATED';

        remoteFallbackIsLoading.value = true;

        if (initialSearchFallbackUrl && initialSearchIsALocatedSearch) {
            remoteFallbackSearchResponse.value = await getFallbackProfiles(initialSearchFallbackUrl);
        } else {
            // There is no fallback search results, then we make sure we clear obsolete fallback results for the previous search
            remoteFallbackSearchResponse.value = undefined;
            europaFallbackSearchResponse.value = undefined;
        }
        remoteFallbackIsLoading.value = false;
    }

    async function fetchEuropaFallbackProfiles() {
        const initialSearchFallbackUrl = initialSearchResponse.value?.fallback;
        const initialSearchIsARemoteSearch = initialSearchResponse.value?.searchType === 'HOST_COUNTRY_REMOTE';
        const remoteFallbackSearchFallbackUrl = remoteFallbackSearchResponse.value?.fallback;

        europaFallbackIsLoading.value = true;
        if (initialSearchFallbackUrl && initialSearchIsARemoteSearch) {
            europaFallbackSearchResponse.value = await getFallbackProfiles(initialSearchFallbackUrl);
        } else if (remoteFallbackSearchFallbackUrl) {
            europaFallbackSearchResponse.value = await getFallbackProfiles(remoteFallbackSearchFallbackUrl);
        } else {
            // There is no fallback search results, then we make sure we clear obsolete fallback results for the previous search
            europaFallbackSearchResponse.value = undefined;
        }
        europaFallbackIsLoading.value = false;
    }

    async function getProfiles(request: SearchProfilesRequest) {
        try {
            return await searchApi.searchProfiles({
                searchid: searchId.value,
                ...request,
            });
        } catch (error) {
            handleSearchProfilesApiError(error as FetchError);
        }
    }

    function getFallbackProfiles(fallbackUrl: string) {
        // extract query params from fallback URL
        const queryParams = useBaseUrl().parseQueryParams(fallbackUrl);
        return getProfiles(queryParams as any as SearchProfilesRequest);
    }

    return {
        searchId: readonlyState(searchId),
        isLoading: readonlyState(isLoading),
        totalResultCount: readonlyState(totalResultCount),
        initialSearchIsLoading: readonlyState(initialSearchIsLoading),
        initialSearchResponse,
        remoteFallbackIsLoading: readonlyState(remoteFallbackIsLoading),
        remoteFallbackSearchResponse,
        europaFallbackIsLoading: readonlyState(europaFallbackIsLoading),
        europaFallbackSearchResponse,
        fetchProfiles,
        fetchRemoteFallbackProfiles,
        fetchEuropaFallbackProfiles,
    };
});
